/**
 * 004-p7 A3.2 (ANA-658) — is it SAFE to START a voice call right now?
 *
 * A surface that auto-starts text (scoping / proactiveStart) must not connect voice
 * before the server resolves the conversation id: `connectVoice` would key the LiveKit
 * room on a fresh `fallbackConversationId` that then diverges from the server id
 * (voice↔text split-brain). The UI disables the voice-start control while this is false.
 *
 * Pure so the readiness rule is unit-testable independent of the `useConversation`
 * hook's transport mocks.
 */
export interface VoiceStartReadyInputs {
  /** The server-resolved text conversation id (`useTextConversation`), or null. */
  conversationId: string | null;
  /** The derived lifecycle phase; `'error'` ⇒ a terminal (e.g. failed `/start`) state. */
  phase: string;
  /** True once a text `/start` has been attempted on this surface. */
  startSessionAttempted: boolean;
  /** True on a surface that will auto-start text once the project resolves. */
  proactiveStart: boolean;
}

/**
 * Ready ⇔ ANY of:
 *  - the server id is committed (`conversationId != null`) — the normal path; OR
 *  - the surface is in a terminal error (`phase === 'error'`) — a failed auto-`/start`
 *    means the id will never resolve, and with NO committed id there is nothing for a
 *    fallback id to diverge from, so voice is safe (it lazy-mints its own id, like home).
 *    Without this the control would dead-lock "Getting ready…" forever (pr-review P2/QA); OR
 *  - the surface will NOT auto-start text (`!startSessionAttempted && !proactiveStart`) —
 *    pure-lazy home, where the shared client id is already authoritative.
 *
 * Guarantees it never latches permanently-false: a text-starting surface resolves the id
 * on success and hits `phase === 'error'` on failure.
 */
export function computeVoiceStartReady(inputs: VoiceStartReadyInputs): boolean {
  const { conversationId, phase, startSessionAttempted, proactiveStart } = inputs;
  return (
    conversationId != null ||
    phase === 'error' ||
    (!startSessionAttempted && !proactiveStart)
  );
}
